<template>
  <v-container fluid>
    <v-row
      class="ma-0"
    >
      <v-col cols="12">
        <v-card
        class="graph__card"
      >
        <v-card-text class="pa-0 ma-0">
          <!-- Filters -->
          <v-container fluid class="pa-4">
            <v-row class="ma-0">
              <v-col cols="12" md="3" lg="3">
                <v-autocomplete
                  v-model="dateRangeFilter"
                  label="Selecione o período para exibição"
                  :items="dateRangeFilterOptions"
                  color="primary"
                  outlined
                  @change="listAmbienceReadings"
                />
              </v-col>
            </v-row>
          </v-container>
          <!-- Content -->
          <v-container fluid class="pa-4">
            <v-row
              v-for="(component, idx) in componentList"
              :key="`ambience-graph-${idx}`"
              class="ma-0"
            >
              <component
                :is="component"
                :probe-data="graphDataList"
              />
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container fluid>
            <v-row class="ma-0">
              <v-btn
                rounded
                @click="() => $router.go(-1)"
              >
                <v-icon small left>fa-chevron-left</v-icon>
                Voltar
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="js">
import moment from "moment"
import {keysToCamel} from "@/common/utils"
import AmbienceTempGraph from "@/pages/LoteProducao/Ambience/AmbienceTempGraph"
import AmbienceHumidityGraph from "@/pages/LoteProducao/Ambience/AmbienceHumidityGraph"
import AmbienceCo2Graph from "@/pages/LoteProducao/Ambience/AmbienceCo2Graph"
import {forEach, includes} from "lodash/collection"
import AmbienceWaterTempGraph from "@/pages/LoteProducao/Ambience/AmbienceWaterTempGraph"
import AmbienceItuGraph from "@/pages/LoteProducao/Ambience/AmbienceItuGraph"
import AmbienceWaterConsumeGraph from "@/pages/LoteProducao/Ambience/AmbienceWaterConsumeGraph"

const AMBIENCE_URL = process.env.VUE_APP_URL_API_AMBIENCIA

const AMBIENCE_DATA_TYPES = {
  TEMPERATURE: "temperature",
  HUMIDITY: "humidity",
  CO2: "co2",
  WATER_TEMPERATURE: "water-temperature",
  ITU: "itu",
  WATER_CONSUME: "water-consume",
}

export default {
  name: "Ambience",
  props: {
    ambienceDataType: {
      type: String,
      required: false
    }
  },
  data: () => ({
    showGraphs: false,
    dateRangeFilter: "24",
    graphDataList: []
  }),
  computed: {
    componentList() {
      switch (this.ambienceDataType) {
        case AMBIENCE_DATA_TYPES.TEMPERATURE:
          return [AmbienceTempGraph]
        case AMBIENCE_DATA_TYPES.HUMIDITY:
          return [AmbienceHumidityGraph]
        case AMBIENCE_DATA_TYPES.CO2:
          return [AmbienceCo2Graph]
        case AMBIENCE_DATA_TYPES.WATER_TEMPERATURE:
          return [AmbienceWaterTempGraph]
        case AMBIENCE_DATA_TYPES.ITU:
          return [AmbienceItuGraph]
        case AMBIENCE_DATA_TYPES.WATER_CONSUME:
          return [AmbienceWaterConsumeGraph]
        default:
          return [
            AmbienceTempGraph,
            AmbienceCo2Graph,
            AmbienceHumidityGraph,
            AmbienceWaterTempGraph,
            AmbienceWaterConsumeGraph,
            AmbienceItuGraph
          ]
      }
    },
    dateRangeFilterOptions() {
      return [
        {
          text: "Últimas 24 horas",
          value: "24",
        },
        {
          text: "Últimas 48 horas",
          value: "48",
        },
        {
          text: "Últimas 72 horas",
          value: "72",
        },
        {
          text: "Últimas 96 horas",
          value: "96",
        },
        {
          text: "Todas as medições",
          value: "ALL",
        },
      ]
    },
    productionBatch() {
      return this.$store.state.loteProducaoAtual
    },
  },
  methods: {
    getLstStrAmbienceNameFilter() {
      switch (this.ambienceDataType) {
        case AMBIENCE_DATA_TYPES.TEMPERATURE:
          return ["TE", "TD", "T_EXTERNA", "T_INTERNA"]
        case AMBIENCE_DATA_TYPES.HUMIDITY:
          return ["UMIDADE"]
        case AMBIENCE_DATA_TYPES.CO2:
          return ["CO"]
        case AMBIENCE_DATA_TYPES.WATER_TEMPERATURE:
          return ["T_AGUA"]
        case AMBIENCE_DATA_TYPES.WATER_CONSUME:
          return ["NIVEL_AGUA"]
        case AMBIENCE_DATA_TYPES.ITU:
          return ["ITU"]
        default:
          return [
            "TE", "TD", "T_EXTERNA", "T_INTERNA",
            "UMIDADE",
            "CO",
            "T_AGUA",
            "NIVEL_AGUA",
            "ITU"
          ]
      }
    },
    async listAmbienceReadings() {
      const mountQueryFilters = () => {
        let queryParams = {}
        queryParams["cd_pavilion_code"] = this.productionBatch.nrPavilhao
        queryParams["cd_official_code"] = this.productionBatch.jsonEstabelecimento.nrCodigoOficial
        queryParams["cd_exploration_unit_code"] = parseInt(
          this.productionBatch.jsonUnidadeExploracao.nrUnidadeExploracao
        )
        queryParams["str_ambience_name"] = this.getLstStrAmbienceNameFilter().join(",")
        if (this.dateRangeFilter !== "ALL") {
          queryParams["ts_probe_registry"] = (
            moment(moment.now()).add(-parseInt(this.dateRangeFilter), "hours")
          ).toISOString()
        }
        return queryParams
      }
      this.graphDataList = []
      const queryParams = mountQueryFilters()
      
      const finalUrl = new URL("ambience-readings", AMBIENCE_URL)
      const resp = await this.$axios.get(
        finalUrl.toString(),
        {
          params: queryParams,
        }
      )
      if (resp.data && resp.data.data) {
        const ambienceReadings = resp.data.data
        if (ambienceReadings.length) {
          this.graphDataList = this.parseAmbienceReadings(ambienceReadings)
        }
      }
    },
    getTempWeekMin() {
      const week = Math.floor(this.productionBatch.nrDiasAlojado / 7)
      switch (week) {
        case 0:
          return 26;
        case 1:
          return 25;
        case 2:
          return 24;
        case 3:
          return 23;
        case 4:
          return 22;
        case 5:
          return 21;
        case 6:
          return 20;
        default:
          return 20;
      }
    },
    getTempWeekMax() {
      const week = Math.floor(this.productionBatch.nrDiasAlojado / 7)
      switch (week) {
        case 0:
          return 30;
        case 1:
          return 29;
        case 2:
          return 28;
        case 3:
          return 27;
        case 4:
          return 26;
        case 5:
          return 25;
        case 6:
          return 24;
        default:
          return 24;
      }
    },
    parseAmbienceReadings(ambienceReadings) {
      ambienceReadings = keysToCamel(ambienceReadings)
      const finalAmbienceReadings = []
      forEach(ambienceReadings, (item) => {
        // Aplicando valores de mínimo e máximo hardcoded para uso no gráfico
        if (includes(["TE", "TD", "T_EXTERNA", "T_INTERNA"], item["strAmbienceName"])) {
          item["flProbeMin"] = this.getTempWeekMin()
          item["flProbeMax"] = this.getTempWeekMax()
        } else if (item["strAmbienceName"] === "CO") {
          item["flProbeMax"] = 1500
          item["flProbeCritical"] = 3000
        } else if (item["strAmbienceName"] === "T_AGUA") {
          item["flProbeMax"] = 22
        } else if (item["strAmbienceName"] === "ITU") {
          item["flProbeMin"] = 65
          item["flProbeMax"] = 80
        }
        item["flProbeAvg"] = parseFloat(parseFloat(item["flProbeAvg"]).toFixed(2))
        
        finalAmbienceReadings.push(item)
      })
      finalAmbienceReadings.sort((a, b) => {
        const firstDate = new Date(a["tsProbeRegistry"])
        const secondDate = new Date(b["tsProbeRegistry"])
        return firstDate - secondDate
      })
      return finalAmbienceReadings
    }
  },
  created() {
    this.listAmbienceReadings()
  }
}
</script>

<style scoped lang="sass">
.graph__card
  border-top: 1px solid #10303D
</style>
